import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useTheme } from "../context/ThemeContext";

function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: "", color: "transparent" };
  let score = 0;
  if (pw.length >= 8)          score++;
  if (pw.length >= 12)         score++;
  if (/[A-Z]/.test(pw))        score++;
  if (/[0-9]/.test(pw))        score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score, label: "Weak",   color: "#ef4444" };
  if (score <= 3) return { score, label: "Fair",   color: "#f59e0b" };
  if (score === 4) return { score, label: "Good",  color: "#3b82f6" };
  return                { score, label: "Strong",  color: "#22c55e" };
}

const BENEFITS = [
  { title:"Live sales dashboard",    desc:"Every sale visible the second it happens" },
  { title:"Agent & shop oversight",  desc:"Know who's performing and who needs support" },
  { title:"Stock control",           desc:"Low-stock alerts before you lose a sale" },
  { title:"Daily email reports",     desc:"Revenue and action items in your inbox every morning" },
  { title:"M-Pesa credit tracking",  desc:"Partial payments and credit reconciled automatically" },
];

function BenefitIcon({ index, color }: { index: number; color: string }) {
  const s = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "none" as const, stroke: color, strokeWidth: "1.75", strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  if (index === 0) return <svg {...s}><path d="M3 17L8 12L12 15L17 9L21 11"/><path d="M17 9H21V13"/></svg>;
  if (index === 1) return <svg {...s}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
  if (index === 2) return <svg {...s}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
  if (index === 3) return <svg {...s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
  return <svg {...s}><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;
}

export default function Register() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const [businessName, setBusinessName] = useState("");
  const [email,        setEmail]        = useState("");
  const [phone,        setPhone]        = useState("");
  const [password,     setPassword]     = useState("");
  const [showPw,       setShowPw]       = useState(false);

  const [agreed,       setAgreed]       = useState(false);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [shake,   setShake]   = useState(false);
  const [success, setSuccess] = useState(false);

  const [referralCode, setReferralCode] = useState("");

  const strength = getPasswordStrength(password);
  const triggerShake = () => { setShake(true); setTimeout(() => setShake(false), 500); };

  const validate = (): string | null => {
    if (!businessName.trim()) return "Business name is required."; 
    if (!email.trim())        return "Email address is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address.";
    if (!password)            return "Password is required.";
    if (password.length < 8)  return "Password must be at least 8 characters.";
    if (!agreed)              return "Please accept the terms to continue.";
    return null;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); triggerShake(); return; }
    setLoading(true); setError("");
  
    const { data, error: signUpError } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(), password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
        data: { role: "supervisor", business_name: businessName.trim(), phone: phone.trim() || null },
      },
    });
  
    if (signUpError) {
      const msg = signUpError.message.toLowerCase();
      if (msg.includes("already registered") || msg.includes("already exists") || msg.includes("already been registered"))
        setError("This email is already registered. Please sign in instead.");
      else if (msg.includes("invalid email")) setError("Please enter a valid email address.");
      else if (msg.includes("password")) setError("Password is too weak. Use at least 8 characters.");
      else setError(signUpError.message);
      setLoading(false);
      triggerShake();
      return;
    }
  
    if (!data.user) {
      setError("Something went wrong. Please try again.");
      setLoading(false);
      triggerShake();
      return;
    }
    
    if (data.user.identities && data.user.identities.length === 0) {
      setError("This email is already registered. Please sign in instead.");
      setLoading(false);
      triggerShake();
      return;
    }
  
    // Generate business code
    const arr = new Uint8Array(4);
    crypto.getRandomValues(arr);
    const businessCode = Array.from(arr, b => b.toString(36)).join("").substring(0, 6).toUpperCase();
  
    // Create profile
    const { error: profileError } = await supabase.from("profiles").upsert({
      id: data.user.id,
      role: "supervisor",
      business_name: businessName.trim(),
      phone: phone.trim() || null,
      internal_email: email.trim().toLowerCase(),
      business_code: businessCode,
      created_at: new Date().toISOString(),
    }, { onConflict: "id" });
  
    if (profileError) {
      console.error("Profile upsert failed:", profileError);
      await supabase.auth.updateUser({
        data: {
          role: "supervisor",
          business_name: businessName.trim(),
          business_code: businessCode,
          phone: phone.trim() || null
        }
      });
    }
  
    // ── Process referral ──────────────────────────────────────────────
    if (referralCode.trim()) {
      console.log("🔄 Processing referral code:", referralCode.trim().toUpperCase());
      try {
        const { data: referrer, error: referrerError } = await supabase
          .from("profiles")
          .select("id, business_name")
          .eq("business_code", referralCode.trim().toUpperCase())
          .maybeSingle();

          console.log("🔍 Referrer lookup result:", { referrer, referrerError });  
  
        if (referrerError) {
          console.error("Error finding referrer:", referrerError);
          setError("Error processing referral code. Please try again.");
        } else if (referrer && referrer.id !== data.user.id) {
          const { data: existingRef } = await supabase
            .from("referrals")
            .select("id")
            .eq("referee_id", data.user.id)
            .maybeSingle();
  
          if (!existingRef) {
            const { error: refError } = await supabase
              .from("referrals")
              .insert({
                referrer_id: referrer.id,
                referee_id: data.user.id,
                created_at: new Date().toISOString(),
                used_for_discount: false,
              });
  
            if (refError) {
              console.error("Error creating referral:", refError);
            } else {
              console.log(`✅ Referral created: ${referrer.business_name} referred ${businessName}`);
            }
          }
        } else if (referrer && referrer.id === data.user.id) {
          console.log("Self-referral not allowed");
        } else {
          console.log("Referral code not found:", referralCode);
        }
      } catch (err) {
        console.error("Error processing referral:", err);
      }
    }

      setLoading(false);
    setSuccess(true);
  };

  // ── theme tokens ────────────────────────────────────────────────
  const D = isDark;
  const pageBg    = D ? "linear-gradient(150deg,#07101f 0%,#0a1628 55%,#07101f 100%)"
                      : "linear-gradient(150deg,#f0f4ff 0%,#e8f0fe 40%,#fef9f0 100%)";
  const cardBg    = D ? "#111c2e" : "#ffffff";
  const formBg    = D ? "#111c2e" : "#ffffff";
  const cardShadow= D ? "0 24px 64px rgba(0,0,0,0.6),0 4px 18px rgba(0,0,0,0.35)"
                      : "0 24px 64px rgba(15,23,42,0.13),0 4px 18px rgba(15,23,42,0.07)";
  const textMain  = D ? "#f1f0ed" : "#0f172a";
  const textSub   = D ? "rgba(241,240,237,0.55)" : "#334155";
  const textMuted = D ? "rgba(241,240,237,0.32)" : "#64748b";
  const divider   = D ? "rgba(255,255,255,0.07)"  : "#e2e8f0";
  const labelClr  = D ? "rgba(241,240,237,0.65)"  : "#1e3a5f";
  const inputBg   = D ? "rgba(255,255,255,0.06)"  : "#f8fafc";
  const inputBdr  = D ? "rgba(255,255,255,0.11)"  : "#cbd5e1";
  const phClr     = D ? "rgba(255,255,255,0.25)"  : "#94a3b8";
  const gridBg    = D ? "rgba(255,255,255,0.05)"  : "#f8fafc";
  const gridBdr   = D ? "rgba(255,255,255,0.09)"  : "#e2e8f0";
  const checkBg   = D ? "rgba(34,197,94,0.1)"     : "#f0fdf4";
  const checkBdr  = D ? "rgba(34,197,94,0.25)"    : "#bbf7d0";
  const tglBg     = D ? "rgba(255,255,255,0.07)"  : "rgba(15,23,42,0.06)";
  const tglBdr    = D ? "rgba(255,255,255,0.13)"  : "rgba(15,23,42,0.1)";
  const tglClr    = D ? "#f1f0ed" : "#0f172a";
  const errBg     = D ? "rgba(220,38,38,0.09)"    : "#fef2f2";
  const errBdr    = D ? "rgba(220,38,38,0.28)"    : "rgba(220,38,38,0.22)";
  const errText   = D ? "#f87171" : "#dc2626";
  const errHint   = D ? "rgba(241,240,237,0.48)"  : "#9a3412";
  const footerBdr = D ? "rgba(255,255,255,0.07)"  : "#e2e8f0";
  const captionClr= D ? "rgba(241,240,237,0.22)"  : "#94a3b8";
  const priceBg   = D ? "rgba(59,130,246,0.08)"   : "#eff6ff";
  const priceBdr  = D ? "rgba(59,130,246,0.2)"    : "#bfdbfe";
  const strBar    = D ? "rgba(255,255,255,0.1)"   : "#e2e8f0";

  const iconClr = D ? "#94a3b8" : "#64748b";

  const svgEye = (visible: boolean) => visible
    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;

  return (
    <div style={{ height:"100dvh", overflowY:"auto", background:pageBg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"12px 16px", fontFamily:"'Inter',sans-serif", transition:"background 0.35s" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,500;0,600;0,700;0,800;1,400&family=Inter:wght@400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        @keyframes riseUp  { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes floatA  { 0%,100%{transform:translateY(0)}    50%{transform:translateY(-10px)} }
        @keyframes floatB  { 0%,100%{transform:translateY(-5px)} 50%{transform:translateY(7px)}  }
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes shake   { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-7px)} 40%,80%{transform:translateX(7px)} }
        @keyframes errIn   { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes success { 0%{transform:scale(0.88);opacity:0} 60%{transform:scale(1.03)} 100%{transform:scale(1);opacity:1} }
        @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.35} }
        .card-l  { animation: riseUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.05s both; }
        .card-r  { animation: riseUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.18s both; }
        .shaking      { animation: shake   0.42s ease !important; }
        .success-card { animation: success 0.45s ease; }
        .err-in       { animation: errIn   0.22s ease; }
        .tgl { transition:all 0.2s; } .tgl:hover { opacity:0.8; }
        .reg-input {
          width:100%; background:${inputBg};
          border:1.5px solid ${inputBdr}; border-radius:10px;
          padding:9px 14px 9px 40px; color:${textMain};
          font-size:13.5px; font-family:'Inter',sans-serif;
          transition:border-color 0.18s,box-shadow 0.18s;
          -webkit-appearance:none; outline:none;
        }
        .reg-input:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,0.14); }
        .reg-input::placeholder { color:${phClr}; }
        .reg-input:disabled { opacity:0.5; cursor:not-allowed; }
        .create-btn {
          width:100%; border:none; border-radius:11px; padding:11px;
          background:linear-gradient(135deg,#1d4ed8 0%,#1e3a5f 100%);
          color:#fff; font-family:'Plus Jakarta Sans',sans-serif; font-weight:700;
          font-size:15px; letter-spacing:0.02em; cursor:pointer;
          box-shadow:0 4px 18px rgba(29,78,216,0.35);
          transition:opacity 0.18s,transform 0.12s,box-shadow 0.18s;
          display:flex; align-items:center; justify-content:center; gap:8px;
          -webkit-tap-highlight-color:transparent;
        }
        .create-btn:hover:not(:disabled) { opacity:0.88; box-shadow:0 7px 24px rgba(29,78,216,0.45); }
        .create-btn:active:not(:disabled){ transform:scale(0.98); }
        .create-btn:disabled { opacity:0.45; cursor:not-allowed; box-shadow:none; }
      `}</style>

      {/* ── Theme toggle ── */}
      <button className="tgl" onClick={toggleTheme} style={{ position:"fixed", top:18, right:18, zIndex:200, background:tglBg, border:`1px solid ${tglBdr}`, borderRadius:40, padding:"7px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:tglClr, fontSize:12, fontFamily:"'Inter',sans-serif", fontWeight:600, backdropFilter:"blur(8px)" }}>
        {D ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>Light</>
           : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>Dark</>}
      </button>

      <div style={{ display:"flex", flexWrap:"wrap", gap:16, width:"100%", maxWidth:920, alignItems:"stretch", justifyContent:"center" }}>

        {/* ══ LEFT — Pitch card ══ */}
        <div className="card-l" style={{ flex:"1 1 300px", maxWidth:420, borderRadius:24, background:cardBg, boxShadow:cardShadow, padding:"32px 28px", display:"flex", flexDirection:"column", transition:"background 0.35s" }}>

          {/* Brand */}
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
            <img src="/Qash.png" alt="Qash" style={{ height:48, objectFit:"contain" }} />
          </div>

          {/* Headline */}
          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:"clamp(22px,3.2vw,30px)", color:textMain, lineHeight:1.12, letterSpacing:"-0.03em", marginBottom:10 }}>
            The smartest investment<br/><span style={{ background:"linear-gradient(90deg,#1d4ed8,#3b82f6)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>your business makes.</span>
          </div>
          <div style={{ fontSize:14, color:textSub, lineHeight:1.8, marginBottom:20, fontWeight:400 }}>
            Stop guessing. Start knowing — every sale, every agent, and every product, always visible.
          </div>

          {/* Benefits */}
          <div style={{ borderTop:`1.5px solid ${divider}`, paddingTop:16, display:"flex", flexDirection:"column", gap:11, marginBottom:20 }}>
            {BENEFITS.map((b, i) => (
              <div key={b.title} style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                <div style={{ width:32, height:32, borderRadius:9, background:gridBg, border:`1px solid ${gridBdr}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
                  <BenefitIcon index={i} color={D ? "#93c5fd" : "#1d4ed8"} />
                </div>
                <div>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:13, color:textMain, marginBottom:1, letterSpacing:"-0.01em" }}>{b.title}</div>
                  <div style={{ fontSize:12, color:textMuted, lineHeight:1.4 }}>{b.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ flex:1 }} />

          {/* Pricing */}
          <div style={{ background:priceBg, border:`1px solid ${priceBdr}`, borderLeft:`4px solid #3b82f6`, borderRadius:"0 10px 10px 0", padding:"13px 16px" }}>
            <div style={{ display:"flex", alignItems:"baseline", gap:5, marginBottom:3 }}>
              <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:22, color:"#3b82f6" }}>14 days</span>
              <span style={{ fontSize:12, color:textMuted }}> free trial</span>
            </div>
            <div style={{ fontSize:12, color:textSub }}>Customer support · No card needed · Cancel anytime</div>
          </div>
        </div>

        {/* ══ RIGHT — Form card ══ */}
        <div className={`card-r${shake ? " shaking" : ""}`} style={{ flex:"1 1 300px", maxWidth:440, borderRadius:24, overflow:"hidden", boxShadow:cardShadow }}>

          {/* Navy gradient header with arch bottom */}
          <div style={{ background:"linear-gradient(145deg,#0f172a 0%,#1e3a5f 55%,#1d4ed8 100%)", padding:"14px 24px 28px", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:-60, right:-60, width:200, height:200, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.07)", pointerEvents:"none" }} />
            <div style={{ position:"absolute", top:-25, right:-25, width:110, height:110, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.05)", pointerEvents:"none" }} />
            <div style={{ position:"absolute", top:22, left:28, width:6, height:6, borderRadius:"50%", background:"rgba(251,191,36,0.7)" }} />
            <div style={{ position:"absolute", top:22, left:40, width:4, height:4, borderRadius:"50%", background:"rgba(251,191,36,0.4)" }} />
            <div style={{ position:"relative" }}>
              <div style={{ fontSize:10, color:"rgba(251,191,36,0.85)", fontFamily:"'Inter',sans-serif", fontWeight:600, letterSpacing:"0.1em", marginBottom:6 }}>OWNER PORTAL</div>
              <div style={{ fontSize:18, fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, color:"#fff", letterSpacing:"-0.03em", lineHeight:1.18, marginBottom:4 }}>Start your free trial</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)", lineHeight:1.5, fontFamily:"'Inter',sans-serif", fontWeight:400 }}>Create your account · No credit card required.</div>
            </div>
            <div style={{ position:"absolute", bottom:-1, left:0, right:0, height:20, background:formBg, borderRadius:"50% 50% 0 0 / 100% 100% 0 0", transition:"background 0.35s" }} />
          </div>

          {/* Form body */}
          <div style={{ background:formBg, padding:"4px 20px 14px", transition:"background 0.35s" }}>

            {success ? (
              /* ── Success ── */
              <div className="success-card" style={{ paddingTop:14, textAlign:"center" }}>
                <div style={{ width:54, height:54, borderRadius:"50%", background:checkBg, border:`2px solid ${checkBdr}`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px", color:"#22c55e" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                </div>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:20, color:textMain, marginBottom:5 }}>Check your email</div>
                <div style={{ color:textSub, fontSize:14, lineHeight:1.7, marginBottom:16 }}>
                  Confirmation link sent to<br/><strong style={{ color:"#3b82f6" }}>{email}</strong>
                </div>
                <div style={{ background: D ? "rgba(59,130,246,0.07)" : "#eff6ff", border:`1px solid ${D ? "rgba(59,130,246,0.18)" : "#bfdbfe"}`, borderRadius:10, padding:"14px 16px", marginBottom:12, textAlign:"left" }}>
                  {["Open your inbox and find the email from QASHUP","Click the confirmation link","You'll land on your dashboard automatically","Your free trial starts the moment you confirm"].map((t,i) => (
                    <div key={i} style={{ display:"flex", gap:8, marginBottom:i<3?8:0, alignItems:"flex-start" }}>
                      <div style={{ width:18, height:18, borderRadius:"50%", background: D ? "rgba(59,130,246,0.15)" : "#dbeafe", border:`1px solid ${D ? "rgba(59,130,246,0.3)" : "#93c5fd"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"#3b82f6", flexShrink:0, marginTop:1, fontFamily:"'Inter',sans-serif", fontWeight:700 }}>{i+1}</div>
                      <span style={{ fontSize:12.5, color:textSub, lineHeight:1.5 }}>{t}</span>
                    </div>
                  ))}
                </div>
                <div style={{ background: D ? "rgba(245,158,11,0.07)" : "#fffbeb", border:`1px solid ${D ? "rgba(245,158,11,0.2)" : "#fcd34d"}`, borderRadius:9, padding:"10px 13px", marginBottom:14, display:"flex", gap:8, alignItems:"flex-start", textAlign:"left" }}>
                  <span style={{ flexShrink:0, display:"flex", color:"#d97706", marginTop:1 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>
                  <span style={{ fontSize:11.5, color:textSub, lineHeight:1.5, fontFamily:"'Inter',sans-serif" }}>Can't find it? Check your <strong style={{ color:"#d97706" }}>Spam or Promotions</strong> folder. Allow up to 2 minutes.</span>
                </div>
                <button onClick={() => navigate("/login")} style={{ width:"100%", padding:"13px", background: D ? "rgba(29,78,216,0.12)" : "#eff6ff", border:`1px solid ${D ? "rgba(59,130,246,0.25)" : "#bfdbfe"}`, borderRadius:11, color:"#3b82f6", fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700, fontSize:14, cursor:"pointer" }}>
                  Go to Sign In →
                </button>
              </div>

            ) : (
              /* ── Form ── */
              <form onSubmit={handleRegister} style={{ display:"flex", flexDirection:"column", gap:7, marginTop:4 }}>

                {/* Field helper */}
                {([
                  { label:"Business / Company Name *", val:businessName, set:setBusinessName, ph:"e.g. Kamau Electronics Ltd", type:"text",  ac:"organization", icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconClr} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
                  { label:"Email Address *",            val:email,        set:setEmail,        ph:"owner@yourbusiness.com",   type:"email", ac:"email",        icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconClr} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> },
                ] as { label:string; val:string; set:(v:string)=>void; ph:string; type:string; ac:string; icon:React.ReactNode }[]).map(f => (
                  <div key={f.label} style={{ display:"flex", flexDirection:"column", gap:3 }}>
                    <label style={{ fontSize:12, fontWeight:600, color:labelClr, paddingLeft:2 }}>{f.label}</label>
                    <div style={{ position:"relative" }}>
                      <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", opacity:0.65, display:"flex" }}>{f.icon}</span>
                      <input className="reg-input" type={f.type} value={f.val} onChange={e => { f.set(e.target.value); setError(""); }} placeholder={f.ph} autoComplete={f.ac} disabled={loading} />
                    </div>
                  </div>
                ))}

                {/* Phone */}
                <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:labelClr, paddingLeft:2 }}>
                    Phone Number 
                  </label>
                  <div style={{ position:"relative" }}>
                    <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", opacity:0.65, display:"flex" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconClr} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                    </span>
                    <input className="reg-input" type="tel" value={phone} onChange={e => { setPhone(e.target.value); setError(""); }} placeholder="e.g. 0712 345 678" autoComplete="tel" disabled={loading} />
                  </div>
                </div>

                {/* Referral Code */}
                  <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                    <label style={{ fontSize:12, fontWeight:600, color:labelClr, paddingLeft:2 }}>
                      Referral Code <span style={{ color:textMuted, fontWeight:400 }}>(optional)</span>
                    </label>
                    <div style={{ position:"relative" }}>
                      <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", opacity:0.65, display:"flex" }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconClr} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                      </span>
                      <input 
                        className="reg-input" 
                        type="text" 
                        value={referralCode} 
                        onChange={e => { 
                          setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6));
                          setError(""); 
                        }} 
                        placeholder="e.g. A7B2C9 (business code of the owner who referred you)" 
                        autoComplete="off"
                        disabled={loading}
                        style={{ paddingLeft:40 }}
                      />
                    </div>
                    {referralCode && (
                      <div style={{ fontSize:11, color: textMuted, paddingLeft:2 }}>
                        {referralCode.length === 6 ? (
                          <span style={{ color: "#22c55e" }}>✓ Valid format - we'll verify this when you sign up</span>
                        ) : (
                          <span style={{ color: "#f59e0b" }}>Business codes are 6 characters (e.g. A7B2C9)</span>
                        )}
                      </div>
                    )}
                  </div>

                {/* Password */}
                <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:labelClr, paddingLeft:2 }}>Password *</label>
                  <div style={{ position:"relative" }}>
                    <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", opacity:0.65, display:"flex" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconClr} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    </span>
                    <input className="reg-input" type={showPw?"text":"password"} value={password} onChange={e => { setPassword(e.target.value); setError(""); }} placeholder="Min 8 characters" autoComplete="new-password" disabled={loading} style={{ paddingRight:42 }} />
                    <button type="button" onClick={() => setShowPw(p=>!p)} tabIndex={-1} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:D?"rgba(255,255,255,0.35)":"#94a3b8", padding:4, display:"flex", alignItems:"center" }}>
                      {svgEye(showPw)}
                    </button>
                  </div>
                  {password && (
                    <div>
                      <div style={{ display:"flex", gap:3, marginBottom:3 }}>
                        {Array.from({length:5}).map((_,i) => <div key={i} style={{ flex:1, height:3, borderRadius:2, background:i<strength.score?strength.color:strBar, transition:"background 0.3s" }} />)}
                      </div>
                      <div style={{ fontSize:11, fontFamily:"'Inter',sans-serif", fontWeight:600, color:strength.color }}>{strength.label}</div>
                    </div>
                  )}
                </div>
            

                            {/* Terms */}
                <label style={{ display:"flex", alignItems:"flex-start", gap:9, cursor:"pointer", userSelect:"none" }}>
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={e => { setAgreed(e.target.checked); setError(""); }}
                    disabled={loading}
                    style={{ width:15, height:15, accentColor:"#3b82f6", cursor:"pointer", marginTop:2, flexShrink:0 }}
                  />
                  <span style={{ fontSize:12, color:textSub, lineHeight:1.6 }}>
                    I agree to the{" "}
                    <Link
                      to="/Terms"
                      onClick={e => e.stopPropagation()}
                      style={{ color:"#3b82f6", textDecoration:"none" }}
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/Privacy"
                      onClick={e => e.stopPropagation()}
                      style={{ color:"#3b82f6", textDecoration:"none" }}
                    >
                      Privacy Policy
                    </Link>
                    . My free trial starts on sign-up.
                  </span>
                </label>

                {error && (
                  <div className="err-in" style={{ background:errBg, border:`1px solid ${errBdr}`, borderRadius:10, padding:"11px 14px", display:"flex", gap:10, alignItems:"flex-start" }}>
                    <span style={{ flexShrink:0, marginTop:1, display:"flex", color:errText }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>
                    <div>
                      <div style={{ color:errText, fontSize:13, fontWeight:600, marginBottom:2, fontFamily:"'Inter',sans-serif" }}>{error}</div>
                      <div style={{ color:errHint, fontSize:11, fontFamily:"'Inter',sans-serif", lineHeight:1.5 }}>Please check the details above and try again.</div>
                    </div>
                  </div>
                )}

                <button type="submit" disabled={loading || !agreed} className="create-btn" style={{ marginTop:4 }}>
                  {loading
                    ? <><span style={{ width:15, height:15, border:"2.5px solid rgba(255,255,255,0.3)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"spin 0.7s linear infinite" }} />Creating account…</>
                    : "Create my free account →"
                  }
                </button>

                <div style={{ paddingTop:8, borderTop:`1px solid ${footerBdr}`, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                  <div style={{ fontSize:13, color:textMuted, textAlign:"center" }}>
                    Already have an account?{" "}
                    <Link to="/login" style={{ color:"#3b82f6", fontWeight:600, textDecoration:"none" }}
                      onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"}
                      onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>
                      Sign in →
                    </Link>
                  </div>
                  <div style={{ fontSize:10, color:captionClr, fontFamily:"'Inter',sans-serif", letterSpacing:"0.05em" }}>
                    QASHUP v1.0 · Secured
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

      </div>

      <div style={{ marginTop:20, fontSize:11, color:captionClr, fontFamily:"'Inter',sans-serif", letterSpacing:"0.04em", textAlign:"center" }}>
        Owner &amp; supervisor access only · Unauthorised access is prohibited
      </div>
    </div>
  );
}
